import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Eye, Trash2, AlertTriangle, CheckCircle2, FileText, Mail, MessageCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const MaintenanceChecklists = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);
  const [sendDialog, setSendDialog] = useState(null);
  const [custEmail, setCustEmail] = useState('');
  const [custName, setCustName] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [custMsg, setCustMsg] = useState('');
  const [sending, setSending] = useState(false);

  const fetchItems = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/maintenance-checklists`;
      if (filter === 'defects') url += '?has_defects=true';
      if (filter === 'ok') url += '?has_defects=false';
      const res = await axios.get(url);
      setItems(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load checklists'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const remove = async (id) => {
    if (!window.confirm('Delete this checklist record?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/maintenance-checklists/${id}`);
      toast.success('Deleted');
      fetchItems();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Delete failed'));
    }
  };

  const previewPdf = (id) => {
    window.open(`${BACKEND_URL}/api/maintenance-checklists/${id}/pdf`, '_blank');
  };

  const openSend = (it) => {
    setSendDialog(it);
    setCustEmail(''); setCustName(''); setCustMsg(''); setCustPhone('');
  };

  const sendEmail = async () => {
    if (!custEmail) { toast.error('Customer email is required'); return; }
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/maintenance-checklists/${sendDialog.id}/send-email`, {
        customer_email: custEmail, customer_name: custName, message: custMsg,
      });
      toast.success(`Email sent to ${custEmail}`);
      setSendDialog(null);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Email failed'));
    } finally {
      setSending(false);
    }
  };

  const sendWhatsApp = () => {
    if (!custPhone) { toast.error('Customer WhatsApp number is required (e.g. 6591234567)'); return; }
    const it = sendDialog;
    const status = it.has_defects ? `⚠️ ${it.defect_count} defect(s)` : '✓ All OK';
    const pdfUrl = `${window.location.origin}${BACKEND_URL.replace(window.location.origin,'')}/api/maintenance-checklists/${it.id}/pdf`;
    const msg = `Dear ${custName || 'Sir/Madam'},

Please find the ${it.frequency} maintenance checklist for vehicle ${it.lorry_plate}.

Checklist No: ${it.checklist_no}
Equipment: ${it.equipment_type || 'Lorry Crane'}
Status: ${status}

${custMsg ? custMsg + '\n\n' : ''}PDF: ${BACKEND_URL}/api/maintenance-checklists/${it.id}/pdf

Best regards,
NJK Logistics Pte Ltd
Tel: 98366186`;
    const phone = custPhone.replace(/\D/g, '');
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    toast.success('WhatsApp opened');
  };

  const resetCounter = async () => {
    if (!window.confirm('Reset the checklist counter? Next submission will start back at CHK-' + new Date().getFullYear() + '-001.\n\n(Existing checklist numbers will NOT change, only the NEXT one onwards.)')) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/maintenance-checklist/reset-counter`);
      toast.success(`Counter reset. Next checklist will be ${res.data.next_number}`);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Reset failed'));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '-';
    try {
      const d = new Date(iso);
      return d.toLocaleString('en-SG', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/billing')} data-testid="back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Maintenance Checklists</h1>
              <p className="text-gray-600">Daily / weekly pre-operation inspection records</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={resetCounter} data-testid="reset-counter-btn" title="Reset sequence number to 001">
              ↻ Reset No.
            </Button>
            <Button onClick={() => window.open('/operator/login', '_blank')} data-testid="new-checklist-btn">
              + New Checklist
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Submitted Checklists</CardTitle>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48" data-testid="filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Submissions</SelectItem>
                <SelectItem value="defects">With Defects</SelectItem>
                <SelectItem value="ok">All OK</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {loading ? <p className="text-gray-500">Loading...</p>
              : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No checklists yet. Operators submit via <code>/operator/login</code>.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Checklist No.</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-left p-3">Lorry</th>
                        <th className="text-left p-3">Operator</th>
                        <th className="text-left p-3">Frequency</th>
                        <th className="text-left p-3">Submitted</th>
                        <th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it) => (
                        <tr key={it.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-mono text-xs font-bold">{it.checklist_no || '-'}</td>
                          <td className="p-3">
                            {it.has_defects ? (
                              <Badge className="bg-red-600 text-white"><AlertTriangle size={12} className="mr-1" />{it.defect_count} defect</Badge>
                            ) : (
                              <Badge className="bg-green-600 text-white"><CheckCircle2 size={12} className="mr-1" />OK</Badge>
                            )}
                          </td>
                          <td className="p-3 font-bold">{it.lorry_plate}</td>
                          <td className="p-3">{it.operator_name}</td>
                          <td className="p-3"><Badge variant="outline">{it.frequency}</Badge></td>
                          <td className="p-3">{fmtDate(it.submitted_at)}</td>
                          <td className="p-3 text-right">
                            <Button size="sm" variant="outline" onClick={() => previewPdf(it.id)} className="mr-1" title="Preview PDF" data-testid={`pdf-${it.id}`}><FileText size={14} className="text-blue-600" /></Button>
                            <Button size="sm" variant="outline" onClick={() => openSend(it)} className="mr-1" title="Send to customer" data-testid={`send-${it.id}`}><Mail size={14} className="text-green-600" /></Button>
                            <Button size="sm" variant="outline" onClick={() => setDetail(it)} className="mr-1"><Eye size={14} /></Button>
                            <Button size="sm" variant="outline" onClick={() => remove(it.id)}><Trash2 size={14} className="text-red-600" /></Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </CardContent>
        </Card>

        <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {detail && (
              <>
                <DialogHeader>
                  <DialogTitle>{detail.checklist_no ? `${detail.checklist_no} — ` : ''}{detail.equipment_type || 'Lorry Crane'} — {detail.lorry_plate} ({detail.frequency})</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 text-sm">
                  <div className="grid grid-cols-2 gap-4 bg-gray-50 p-3 rounded">
                    <div><span className="text-gray-500">Operator:</span> <strong>{detail.operator_name}</strong></div>
                    <div><span className="text-gray-500">Submitted:</span> {fmtDate(detail.submitted_at)}</div>
                    <div><span className="text-gray-500">Status:</span> {detail.has_defects ? <span className="text-red-600 font-bold">{detail.defect_count} defect(s)</span> : <span className="text-green-600 font-bold">All OK</span>}</div>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 w-12">S/N</th>
                        <th className="text-left p-2">Item</th>
                        <th className="text-left p-2 w-24">Status</th>
                        <th className="text-left p-2">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((it, i) => (
                        <tr key={i} className={`border-b ${it.status === 'no' ? 'bg-red-50' : ''}`}>
                          <td className="p-2 font-mono text-xs">{it.item_no || ''}</td>
                          <td className="p-2">{it.item_text || it.item || ''}</td>
                          <td className="p-2">
                            <Badge className={
                              it.status === 'yes' ? 'bg-green-600 text-white' :
                              it.status === 'no' ? 'bg-red-600 text-white' : 'bg-gray-500 text-white'
                            }>{(it.status || '').toUpperCase()}</Badge>
                          </td>
                          <td className="p-2 text-gray-600">{it.remarks || it.comment || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(detail.vehicle_reg_no || detail.lifting_machine_no) && (
                    <div className="grid grid-cols-2 gap-3 bg-gray-50 p-3 rounded text-sm">
                      <div><span className="text-gray-500">Vehicle Reg No:</span> <strong>{detail.vehicle_reg_no || '-'}</strong></div>
                      <div><span className="text-gray-500">Lifting Machine No:</span> <strong>{detail.lifting_machine_no || '-'}</strong></div>
                      {detail.supervisor_name && <div><span className="text-gray-500">Supervisor:</span> <strong>{detail.supervisor_name}</strong></div>}
                    </div>
                  )}
                  {detail.overall_notes && (
                    <div className="bg-yellow-50 p-3 rounded">
                      <strong>Overall Notes:</strong><br/>{detail.overall_notes}
                    </div>
                  )}
                  {detail.signature && (
                    <div>
                      <strong>Operator Signature:</strong>
                      <img src={detail.signature} alt="signature" className="border rounded mt-1 max-w-xs" />
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={!!sendDialog} onOpenChange={(o) => !o && setSendDialog(null)}>
          <DialogContent className="max-w-lg">
            {sendDialog && (
              <>
                <DialogHeader>
                  <DialogTitle>Send {sendDialog.checklist_no} to Customer</DialogTitle>
                </DialogHeader>
                <div className="space-y-3 py-3">
                  <div className="bg-blue-50 p-3 rounded text-sm">
                    <FileText className="inline w-4 h-4 mr-1 text-blue-600" />
                    {sendDialog.equipment_type || 'Lorry Crane'} — {sendDialog.lorry_plate} ({sendDialog.frequency})
                  </div>
                  <div>
                    <label className="text-sm font-medium">Customer Name</label>
                    <Input value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="e.g. Mr Lee" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Customer Email</label>
                    <Input type="email" value={custEmail} onChange={(e) => setCustEmail(e.target.value)} placeholder="customer@company.com" data-testid="cust-email" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Customer WhatsApp (e.g. 6591234567)</label>
                    <Input value={custPhone} onChange={(e) => setCustPhone(e.target.value)} placeholder="6591234567" data-testid="cust-phone" />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Optional Message</label>
                    <Textarea value={custMsg} onChange={(e) => setCustMsg(e.target.value)} rows={2} placeholder="Additional note for the customer..." />
                  </div>
                  <div className="flex gap-2 pt-2">
                    <Button onClick={() => previewPdf(sendDialog.id)} variant="outline" className="flex-1" data-testid="dlg-preview">
                      <FileText size={14} className="mr-1" /> Preview PDF
                    </Button>
                    <Button onClick={sendEmail} disabled={sending} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="dlg-email">
                      <Mail size={14} className="mr-1" /> {sending ? 'Sending...' : 'Send Email'}
                    </Button>
                    <Button onClick={sendWhatsApp} className="flex-1 bg-[#25D366] hover:bg-[#1ebe5d]" data-testid="dlg-wa">
                      <MessageCircle size={14} className="mr-1" /> WhatsApp
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MaintenanceChecklists;

